import React, { FC, useEffect, useRef, useState } from 'react';
import { MdClose } from 'react-icons/md';

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useToast } from '@chakra-ui/react';

import {
  addPurchaseOrderAttachment,
  deletePurchaseOrderAttachment,
  fetchPurchaseOrderAttachmentUrl,
} from 'api/purchaseOrder';
import { getAttachmentDisplayName } from 'utils/s3';

import { PurchaseOrderAttachmentsModalProps } from './PurchaseOrderAttachmentsModal.props';

const PurchaseOrderAttachmentsModalView: FC<PurchaseOrderAttachmentsModalProps> = ({
  isOpen,
  onClose,
  purchaseOrder,
  onPurchaseOrderUpdated,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cancelDeleteRef = useRef<HTMLButtonElement>(null);
  const attachments = purchaseOrder?.attachments ?? [];
  const [selectedAttachment, setSelectedAttachment] = useState<string>();
  const [attachmentToDelete, setAttachmentToDelete] = useState<string>();

  useEffect(() => {
    setSelectedAttachment(purchaseOrder?.attachments?.[0]);
  }, [purchaseOrder?._id]);

  const { mutate: uploadAttachment, isLoading: isUploading } = useMutation(
    (file: File) => addPurchaseOrderAttachment(purchaseOrder?._id || '', file),
    {
      onSuccess: (data) => {
        const updatedPurchaseOrder = data?.item;
        if (updatedPurchaseOrder) {
          onPurchaseOrderUpdated?.(updatedPurchaseOrder);
          const nextAttachments = updatedPurchaseOrder.attachments ?? [];
          setSelectedAttachment(nextAttachments[nextAttachments.length - 1]);
        }

        queryClient.invalidateQueries(['fetch-purchase-orders']);
        queryClient.invalidateQueries(['purchase-order-attachment-url']);
        toast({
          status: 'success',
          title: 'Attachment uploaded',
          isClosable: true,
          duration: 3000,
        });
      },
      onError: (error: Error) => {
        toast({
          status: 'error',
          title: 'Failed to upload attachment',
          description: error?.message || 'Something went wrong.',
          isClosable: true,
          duration: 5000,
        });
      },
    },
  );

  const { mutate: deleteAttachment, isLoading: isDeleting } = useMutation(
    (key: string) =>
      deletePurchaseOrderAttachment(purchaseOrder?._id || '', key),
    {
      onSuccess: (data, deletedKey) => {
        const updatedPurchaseOrder = data?.item;
        const nextAttachments = updatedPurchaseOrder?.attachments ?? [];

        if (updatedPurchaseOrder) {
          onPurchaseOrderUpdated?.(updatedPurchaseOrder);
        }

        setSelectedAttachment((current) => {
          if (current !== deletedKey) {
            return current;
          }

          return nextAttachments[0];
        });
        setAttachmentToDelete(undefined);
        queryClient.invalidateQueries(['fetch-purchase-orders']);
        queryClient.invalidateQueries(['purchase-order-attachment-url']);
        toast({
          status: 'success',
          title: 'Attachment deleted',
          isClosable: true,
          duration: 3000,
        });
      },
      onError: (error: Error) => {
        toast({
          status: 'error',
          title: 'Failed to delete attachment',
          description: error?.message || 'Something went wrong.',
          isClosable: true,
          duration: 5000,
        });
      },
    },
  );

  const {
    data: selectedUrl,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useQuery(
    ['purchase-order-attachment-url', purchaseOrder?._id, selectedAttachment],
    () =>
      fetchPurchaseOrderAttachmentUrl(
        purchaseOrder?._id || '',
        selectedAttachment || '',
      ),
    {
      enabled: Boolean(isOpen && purchaseOrder?._id && selectedAttachment),
      staleTime: 4 * 60 * 1000,
    },
  );

  return (
    <>
      <Modal
        size="6xl"
        isCentered
        isOpen={isOpen}
        onClose={onClose}
        scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="10px" maxW="1100px" maxH="90vh">
          <ModalCloseButton _focus={{ outline: 'none' }} />
          <ModalHeader mt={4} textAlign="center">
            Attachments
            {purchaseOrder?.poNumer ? ` - ${purchaseOrder.poNumer}` : ''}
          </ModalHeader>
          <ModalBody pb={8}>
            <Flex gap={6} minH="520px" align="stretch">
              <Flex
                w="280px"
                flexShrink={0}
                direction="column"
                justify="space-between">
                <Flex direction="column" gap={3} overflowY="auto" pr={1}>
                  {attachments.length === 0 ? (
                    <Center p={4}>
                      <Text fontSize="sm" color="gray.500" textAlign="center">
                        No attachments yet
                      </Text>
                    </Center>
                  ) : (
                    attachments.map((attachment) => {
                      const isSelected = attachment === selectedAttachment;

                      return (
                        <Box
                          key={attachment}
                          p={4}
                          pr={8}
                          position="relative"
                          cursor="pointer"
                          border="1px solid"
                          borderColor={isSelected ? 'primary.500' : 'gray.200'}
                          bg={isSelected ? '#83FF9C80' : 'white'}
                          borderRadius="8px"
                          onClick={() => setSelectedAttachment(attachment)}>
                          <IconButton
                            size="xs"
                            top={1}
                            right={1}
                            position="absolute"
                            variant="ghost"
                            aria-label="Delete attachment"
                            icon={<MdClose size="16px" />}
                            onClick={(event) => {
                              event.stopPropagation();
                              setAttachmentToDelete(attachment);
                            }}
                          />
                          <Text
                            fontWeight="semibold"
                            noOfLines={2}
                            fontSize="sm">
                            {getAttachmentDisplayName(attachment)}
                          </Text>
                        </Box>
                      );
                    })
                  )}
                </Flex>

                <Button
                  mt={4}
                  w="100%"
                  isLoading={isUploading}
                  isDisabled={!purchaseOrder?._id}
                  onClick={() => fileInputRef.current?.click()}>
                  Attach PDF
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file && purchaseOrder?._id) {
                      uploadAttachment(file);
                    }
                    event.target.value = '';
                  }}
                />
              </Flex>

              <Box
                flex={1}
                minH="500px"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="8px"
                overflow="hidden"
                bg="gray.50">
                {isPreviewLoading ? (
                  <Center h="100%">
                    <Spinner />
                  </Center>
                ) : isPreviewError ? (
                  <Center h="100%" px={6}>
                    <Text color="gray.500" textAlign="center">
                      Unable to load this attachment.
                    </Text>
                  </Center>
                ) : selectedUrl ? (
                  <iframe
                    title="Purchase order attachment preview"
                    src={selectedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 'none', minHeight: '500px' }}
                  />
                ) : (
                  <Center h="100%">
                    <Text color="gray.500">
                      Select an attachment to preview
                    </Text>
                  </Center>
                )}
              </Box>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
      <AlertDialog
        isCentered
        isOpen={Boolean(attachmentToDelete)}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => {
          if (!isDeleting) {
            setAttachmentToDelete(undefined);
          }
        }}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete attachment
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete{' '}
              {attachmentToDelete
                ? getAttachmentDisplayName(attachmentToDelete)
                : 'this attachment'}
              ? This will remove the file from storage.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelDeleteRef}
                variant="outline"
                isDisabled={isDeleting}
                onClick={() => setAttachmentToDelete(undefined)}>
                No
              </Button>
              <Button
                ml={3}
                colorScheme="red"
                isLoading={isDeleting}
                onClick={() => {
                  if (attachmentToDelete) {
                    deleteAttachment(attachmentToDelete);
                  }
                }}>
                Yes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default PurchaseOrderAttachmentsModalView;
